/* eslint-disable import/extensions */
// eslint-disable-next-line no-use-before-define
import * as React from 'react';
import { inject } from 'mobx-react';

import { ErrorBoundary } from 'bernie-view';
import { RouteStore } from 'bernie-core';

import { useDialog } from '@shared-ui/dialog-context';
import { useLocalization } from '@shared-ui/localization-context';

// eslint-disable-next-line import/no-unresolved
import { SearchCriteriaModel } from 'stores/models';

import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkSpacing } from 'uitk-react-spacing';

import { buildSubmitData, OnChangeFn, OnSubmitFn } from './action-provider';
import { SharedUiDateFieldWrapper, SharedUiSubmitButtonWrapper, SharedUiTravelersWrapper } from './field-wrappers';
import { SharedUiFormWrapper } from './form-wrapper';

export const SharedUiDatedForm: React.FC<{ routeStore?: RouteStore; onChange?: OnChangeFn }> = inject('routeStore')(
  ({ routeStore, onChange }) => {
    const { formatText } = useLocalization();

    return (
      <ErrorBoundary label="shared-ui-dated-form">
        <SharedUiFormWrapper action={routeStore.pathname} onChange={onChange}>
          <UitkLayoutFlex space="three">
            <UitkLayoutFlexItem grow={2}>
              <div>
                <SharedUiDateFieldWrapper buttonText={formatText('hotels.infosite.searchForm.submit.label')} />
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem minWidth="third_width">
              <div>
                <SharedUiTravelersWrapper closeOnSubmit={false} />
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem grow={1}>
              <div>
                <SharedUiSubmitButtonWrapper text={formatText('hotels.infosite.searchForm.submit.label')} />
              </div>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </SharedUiFormWrapper>
      </ErrorBoundary>
    );
  }
);
SharedUiDatedForm.displayName = 'SharedUiDatedForm';

export const SharedUiSingleUnitForm: React.FC<{
  routeStore?: RouteStore;
  onChange?: OnChangeFn;
  onSubmit?: OnSubmitFn;
  showFormBtn?: boolean;
}> = inject('routeStore')(({ routeStore, onChange, onSubmit, showFormBtn }) => {
  const { formatText } = useLocalization();

  return (
    <SharedUiFormWrapper action={routeStore.pathname} onChange={onChange} onSubmit={onSubmit}>
      <UitkLayoutFlex direction="column" space="three">
        <UitkLayoutFlexItem>
          <div>
            <UitkLayoutFlex space="three" wrap="wrap">
              <UitkLayoutFlexItem grow={2}>
                <div>
                  <SharedUiDateFieldWrapper
                    buttonText={
                      showFormBtn ? formatText('hotels.roomPicker.doneButton.label') : formatText('hotels.infosite.searchForm.submit.label')
                    }
                    closeOnSubmit={false}
                  />
                </div>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </div>
        </UitkLayoutFlexItem>
        <UitkLayoutFlexItem>
          <div>
            <UitkLayoutFlex space="three">
              <UitkLayoutFlexItem minWidth="third_width" grow={2}>
                <div>
                  <SharedUiTravelersWrapper closeOnSubmit={false} />
                </div>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </div>
        </UitkLayoutFlexItem>
        <UitkLayoutFlexItem>
          {showFormBtn && (
            <div>
              <SharedUiSubmitButtonWrapper text={formatText('hotels.infosite.searchForm.availabilityButton.label')} />
            </div>
          )}
        </UitkLayoutFlexItem>
      </UitkLayoutFlex>
    </SharedUiFormWrapper>
  );
});
SharedUiSingleUnitForm.displayName = 'SharedUiSingleUnitForm';

export const SharedUiDatelessForm: React.FC<{ routeStore?: RouteStore; onSubmit?: OnSubmitFn }> = inject('routeStore')(
  ({ routeStore, onSubmit }) => {
    const [isMounted, setIsMounted] = React.useState(false);
    const { formatText } = useLocalization();

    React.useEffect(() => {
      setIsMounted(true);
    }, []);

    return (
      <div className="uitk-cell">
        <ErrorBoundary label="shared-ui-dateless-form">
          <SharedUiFormWrapper action={isMounted ? routeStore.pathname : '/Hotel-Search'} onSubmit={onSubmit}>
            <UitkLayoutFlex space="three">
              <UitkLayoutFlexItem grow={2}>
                <div>
                  <SharedUiDateFieldWrapper buttonText={formatText('hotels.roomPicker.doneButton.label')} />
                </div>
              </UitkLayoutFlexItem>
              <UitkLayoutFlexItem minWidth="third_width">
                <div>
                  <SharedUiTravelersWrapper />
                </div>
              </UitkLayoutFlexItem>
              <UitkLayoutFlexItem grow={1}>
                <div>
                  <SharedUiSubmitButtonWrapper text={formatText('hotels.infosite.searchForm.availabilityButton.label')} />
                </div>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </SharedUiFormWrapper>
        </ErrorBoundary>
      </div>
    );
  }
);
SharedUiDatelessForm.displayName = 'SharedUiDatelessForm';

export const SharedUiDatelessFormMobile: React.FC<{ routeStore?: RouteStore; onSubmit?: OnSubmitFn }> = inject('routeStore')(
  ({ routeStore, onSubmit, children }) => {
    const formRef = React.useRef<HTMLFormElement>(null);
    const [isMounted, setIsMounted] = React.useState(false);
    const [rewriteHistory, setRewriteHistory] = React.useState(false);
    const openDatePickerFromTravelers = React.useRef(false);
    const { formatText } = useLocalization();

    const [, datePickerDialogActions] = useDialog('datepicker-start');

    React.useEffect(() => {
      setIsMounted(true);
    }, []);

    const action = isMounted ? routeStore.pathname : '/Hotel-Search';

    const onChange = (searchCriteria: SearchCriteriaModel, field: string): void => {
      if (field === 'travelers') {
        datePickerDialogActions.openDialog();
        setRewriteHistory(true);
        openDatePickerFromTravelers.current = true;

        return;
      }

      if (field === 'dates') {
        if (formRef.current) {
          onSubmit(buildSubmitData(new FormData(formRef.current), action, searchCriteria));
        }
      }
    };

    const goBackInHistoryTwice = (): void => {
      history.go(-2);
    };

    // Go back twice in history if you're coming from the guest selector to avoid going
    // back to the guest selector when clicking the X button.
    const onDismiss = rewriteHistory ? goBackInHistoryTwice : undefined;

    // Go back twice in history if you're coming from the guest selector to avoid going
    // back to the guest selector when there is error in date picker.
    // Else go back in history once whenever there is an error in dialog to close the dialog.
    const onError = (): void => {
      if (openDatePickerFromTravelers.current) {
        goBackInHistoryTwice();
        openDatePickerFromTravelers.current = false;
      } else {
        history.go(-1);
      }
    };

    // This will reset the rewriteHistory state to false when the X button is clicked
    // and when a user goes back in the browser history.
    const cleanupRewrite = (): void => {
      setRewriteHistory(false);
      window.removeEventListener('popstate', cleanupRewrite);
    };

    if (rewriteHistory) {
      window.addEventListener('popstate', cleanupRewrite);
    }

    return (
      <ErrorBoundary label="shared-ui-dateless-mobile-form">
        <SharedUiFormWrapper action={action} onChange={onChange} formRef={formRef} onError={onError}>
          <div>
            <SharedUiDateFieldWrapper
              buttonText={formatText('hotels.infosite.searchForm.availabilityButton.label')}
              closeOnSubmit={false}
              onDismiss={onDismiss}
              invalidDatesText={formatText('hotels.search.validation.invalid.dateless.text')}
            />
          </div>
          <UitkSpacing padding={{ blockstart: 'two' }}>
            <div>
              <SharedUiTravelersWrapper closeOnSubmit={false} />
            </div>
          </UitkSpacing>
          {!isMounted && (
            <UitkSpacing margin={{ blockstart: 'two' }}>
              <div>
                <SharedUiSubmitButtonWrapper text={formatText('hotels.infosite.searchForm.availabilityButton.label')} />
              </div>
            </UitkSpacing>
          )}
          {children}
        </SharedUiFormWrapper>
      </ErrorBoundary>
    );
  }
);
SharedUiDatelessFormMobile.displayName = 'SharedUiDatelessFormMobile';
